import type { ChangeEvent, FocusEvent, InputHTMLAttributes } from 'react';
import { Icon } from '../../icons/Icon';
import { useFieldControl } from '../Field/fieldContext';
import styles from './Input.module.css';

/**
 * §5's three Input heights, named for the job rather than for a t-shirt size, because that is how
 * the row names them: `layout/control-h` for form fields, `layout/control-h-search` for the search
 * box (which also gets the leading glyph), `layout/control-h-compact` for dense surfaces.
 */
export const INPUT_SIZES = ['form', 'search', 'compact'] as const;
export type InputSize = (typeof INPUT_SIZES)[number];

/**
 * The single-line native types this control covers. Checkboxes, radios and selects are their own
 * components — each needs different chrome and a different disabled mechanism, so one component
 * spanning them would be a switch statement wearing a costume.
 *
 * `date` and `time` are in the list but are normally reached through `DateInput`, which composes
 * this component and adds what a date needs beyond a text box: ISO `min`/`max`, and §5's rule that
 * a RANGE is two labelled inputs rather than a popover.
 */
export const INPUT_TYPES = [
    'text',
    'email',
    'search',
    'tel',
    'url',
    'number',
    'password',
    'date',
    'time',
] as const;
export type InputType = (typeof INPUT_TYPES)[number];

/*
 * `size` is omitted from the native attributes and re-declared: HTML's own `size` is a character
 * count, and a component that took both would have two props one letter apart meaning different
 * things. `className` and `disabled` are omitted for the same reasons as on Button — see the notes
 * there and in the component body below.
 */
type NativeInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'className' | 'disabled' | 'size' | 'type'
>;

export interface InputProps extends NativeInputProps {
    readonly size?: InputSize;
    readonly type?: InputType;
    /** Rendered as `aria-disabled` + `readOnly` — see the component body. */
    readonly disabled?: boolean;
    /**
     * Marks the control invalid on its own. Inside a `Field` this is usually unnecessary — the
     * field supplies it along with the message the control is described by.
     */
    readonly invalid?: boolean;
    /**
     * The test hook (`coding-standards.md §4`), forwarded to the real `<input>`.
     *
     * `| undefined` explicitly: `DateRangeInput` derives its two hooks from its own optional one
     * (`${testId}-from`), so it hands this prop a possibly-undefined value rather than omitting it,
     * and `exactOptionalPropertyTypes` treats those as different things.
     */
    readonly 'data-testid'?: string | undefined;
}

/**
 * A real `<input>` (FS-3 FR-C2's native-first posture), styled entirely from the §2–§4 tokens.
 *
 * **Disabled is `aria-disabled` plus `readOnly`, never the bare `disabled` attribute.** §5's matrix
 * and §9's permission rule require the control to stay focusable so a keyboard user can reach it
 * and discover why it is unavailable — which the native attribute forbids by dropping it out of the
 * tab order. But `aria-disabled` alone is a label with no teeth on a text field: the input still
 * accepts typing. `readOnly` is the native half that actually stops the edit while keeping the
 * control focusable, so the pair says the same thing to the accessibility tree and to the browser.
 * (Button has no equivalent — it guards its click handler instead. Each native element refuses
 * input in its own way, which is the cost of building on them rather than on divs.)
 *
 * Everything about validation arrives from the surrounding `Field` through context — the id the
 * label points at, `aria-describedby`, `aria-invalid`, and the blur/change reports that drive §5's
 * timing. Outside a `Field` the hook returns the control's own props untouched and this is an
 * ordinary input, which is what a topbar search box wants.
 */
export function Input({
    size = 'form',
    type = 'text',
    disabled = false,
    invalid,
    id,
    required,
    'aria-describedby': describedBy,
    'data-testid': testId,
    onBlur,
    onChange,
    ...rest
}: InputProps) {
    const field = useFieldControl({ id, 'aria-describedby': describedBy, invalid, required });

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        onBlur?.(event);
        field.reportBlur(event.target.value);
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (disabled) {
            return;
        }
        onChange?.(event);
        field.reportChange(event.target.value);
    }

    return (
        <span className={`${styles.shell} ${styles[size]}`}>
            {size === 'search' ? (
                // Decorative — the field's label or the input's own accessible name carries the
                // meaning, so a second announcement of "search" would be noise (see Icon).
                <span className={styles.leading}>
                    <Icon name="search" size="sm" />
                </span>
            ) : null}
            <input
                {...rest}
                aria-describedby={field['aria-describedby']}
                aria-disabled={disabled}
                aria-invalid={field['aria-invalid']}
                className={styles.input}
                data-testid={testId}
                id={field.id}
                onBlur={handleBlur}
                onChange={handleChange}
                readOnly={disabled || rest.readOnly}
                required={field.required}
                type={type}
            />
        </span>
    );
}
